import { AbstractControl, FormControl } from '@angular/forms';
import { CartLine, DistributionChainState } from '@next/core-lib/cart';
import { FeatureFlags } from '@next/core-lib/featureflag';
import { TableColumn, TableColumnTypes } from '@next/core-lib/table';
import { CartColumns } from 'src/app/cart/models/app.models';

import { decorateFormControl } from 'src/app/product/helpers/add-to-cart-helpers';

import { createQuantityUnitSuffixArray } from './create-quantity-unit-suffix';
import { CartComponent } from '../../cart.component';

export const createSalesOrderColumns = (
  componentRef: CartComponent,
  hasAtLeastAdvisorRole: boolean,
  featureFlags: string[],
  isTabOrSmallerDisplay: boolean,
  hasRemarksFeature: boolean,
): TableColumn[] => {
  const salesOrderColumns = [
    {
      id: CartColumns.skuTitle,
      type: TableColumnTypes.TEMPLATE,
      label: `cart.cart-column-${CartColumns.skuTitle}`,
      template: componentRef.columnSkuTitleRef.instance.template,
      className: 'flex__child--2',
      headerClassName: 'flex__child--2',
      disableSort: true,
    },
    {
      id: CartColumns.quantity,
      type: componentRef.editable ? TableColumnTypes.INPUT_NUMBER : TableColumnTypes.NUMBER,
      label: `cart.cart-column-${CartColumns.quantityAndUnitInformation}`,
      inputConfig: componentRef.editable && {
        callback: (row: CartLine, control: AbstractControl) => componentRef.debounceQuantityChange$.next({ row, control }),
        formArray: componentRef.orderQuantities,
        step: componentRef.getQuantityStep,
        decorateFormControl: (row: CartLine, control: FormControl) => decorateFormControl(row, control, componentRef.locale),
        suffixArray: createQuantityUnitSuffixArray(componentRef),
      },
      numberConfig: !componentRef.editable && {
        suffixArray: createQuantityUnitSuffixArray(componentRef),
      },
      className: `flex__child--2 spacing--padding-half-vertical${!componentRef.editable ? ' spacing--margin-right' : ''}`,
      headerClassName: `flex__child--2${!componentRef.editable ? ' spacing--margin-right' : ''}`,
      disableSort: true,
    },
    {
      id: CartColumns.siloDistribution,
      type: TableColumnTypes.TEMPLATE,
      label: `cart.cart-column-${CartColumns.siloDistribution}`,
      template: componentRef.columnSiloDistributionRef.instance.template,
      className: 'flex__child--2',
      headerClassName: 'flex__child--2',
      disableSort: true,
    },
    {
      id: CartColumns.productSupplements,
      type: TableColumnTypes.TEMPLATE,
      label: `cart.cart-column-${CartColumns.productSupplements}`,
      template: componentRef.columnProductSupplementsRef.instance.template,
      className: 'flex__child--3',
      headerClassName: 'flex__child--3',
      disableSort: true,
    },
    {
      id: CartColumns.deliveryInformation,
      type: TableColumnTypes.TEMPLATE,
      label: `cart.cart-column-${CartColumns.deliveryInformation}`,
      template: componentRef.columnDeliveryDateRef.instance.template,
      className: 'flex__child--2',
      headerClassName: 'flex__child--2',
      disableSort: true,
    },
  ];

  if (
    componentRef.cart.lines.some(
      (line) =>
        line.orderingInformation.distributionChainState === DistributionChainState.BlockedForReturn ||
        line.orderingInformation.distributionChainState === DistributionChainState.TemporaryReturnBlockWebshop,
    )
  ) {
    salesOrderColumns.push({
      id: CartColumns.blockedForReturn,
      type: TableColumnTypes.TEMPLATE,
      label: '' /* Intentionally empty */,
      template: componentRef.columnBlockedForReturnRef.instance.template,
      className: 'flex__child--2',
      headerClassName: 'flex__child--2',
      disableSort: true,
    });
  }

  if (
    (hasAtLeastAdvisorRole && featureFlags.includes(FeatureFlags.enableCartLineRemarksAdvisors)) ||
    featureFlags.includes(FeatureFlags.enableCartLineRemarksCustomers) ||
    hasRemarksFeature
  ) {
    salesOrderColumns.push({
      id: CartColumns.remarks,
      type: TableColumnTypes.TEMPLATE,
      label: `cart.cart-column-${CartColumns.remarks}`,
      template: componentRef.columnRemarksRef.instance.template,
      className: 'flex__child--1',
      headerClassName: 'flex__child--1',
      disableSort: true,
    });
  }

  if (hasAtLeastAdvisorRole && componentRef.showPrice) {
    salesOrderColumns.push({
      id: CartColumns.priceResults,
      type: TableColumnTypes.TEMPLATE,
      label: `cart.cart-column-${CartColumns.priceResults}`,
      template: componentRef.columnPriceResultsRef.instance.template,
      className: 'flex__child--1',
      headerClassName: 'flex__child--1',
      disableSort: true,
    });
  }

  if (isTabOrSmallerDisplay) {
    const mobileColumnsOrder = [
      CartColumns.skuTitle,
      CartColumns.quantity,
      CartColumns.deliveryInformation,
      CartColumns.priceResults,
      CartColumns.remarks,
      CartColumns.siloDistribution,
      CartColumns.productSupplements,
      CartColumns.blockedForReturn,
    ];
    salesOrderColumns.sort((a, b) => mobileColumnsOrder.indexOf(a.id) - mobileColumnsOrder.indexOf(b.id));
  }

  return salesOrderColumns;
};
